import React, { useState, useEffect, useRef } from "react";
import { base44 } from "@/api/base44Client";
import {
  Sparkles,
  CheckCircle,
  AlertTriangle,
  Target,
  ArrowRight,
} from "lucide-react";

const LEVEL_CONFIG = {
  0: { label: "Not Started", next: 1, nextLabel: "Level 1 — Committed" },
  1: { label: "Committed", next: 2, nextLabel: "Level 2 — Employer" },
  2: { label: "Employer", next: 3, nextLabel: "Level 3 — Leader" },
  3: { label: "Leader", next: null, nextLabel: "Maintain Excellence" },
};

export default function AISummary({
  latestResult,
  tasks,
  evidence,
  currentLevel,
}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestKeyRef = useRef(null);
  const inFlightRef = useRef(false);
  const unavailableRef = useRef(false);

  const requestKey = JSON.stringify({
    currentLevel,
    result: latestResult && {
      id: latestResult.id,
      maturity_score: latestResult.maturity_score,
      level1_score: latestResult.level1_score,
      level2_score: latestResult.level2_score,
      level3_score: latestResult.level3_score,
      gaps: latestResult.gaps,
      risk_flags: latestResult.risk_flags,
    },
    tasks: tasks.map(({ id, level, status }) => ({ id, level, status })),
    evidence: evidence.map(({ id, linked_level }) => ({ id, linked_level })),
  });

  useEffect(() => {
    if (
      unavailableRef.current ||
      inFlightRef.current ||
      requestKeyRef.current === requestKey
    )
      return;
    requestKeyRef.current = requestKey;
    inFlightRef.current = true;
    generateSummary().finally(() => {
      inFlightRef.current = false;
    });
  }, [requestKey]);

  async function generateSummary() {
    setLoading(true);
    try {
      const taskStats = {
        total: tasks.length,
        complete: tasks.filter((t) => t.status === "complete").length,
        level1: tasks.filter((t) => t.level === 1).length,
        level2: tasks.filter((t) => t.level === 2).length,
        level3: tasks.filter((t) => t.level === 3).length,
        level1Complete: tasks.filter(
          (t) => t.level === 1 && t.status === "complete",
        ).length,
        level2Complete: tasks.filter(
          (t) => t.level === 2 && t.status === "complete",
        ).length,
      };

      const evidenceByLevel = {
        level1: evidence.filter((e) => e.linked_level === 1).length,
        level2: evidence.filter((e) => e.linked_level === 2).length,
        level3: evidence.filter((e) => e.linked_level === 3).length,
      };

      const prompt = `You are a Disability Confident scheme advisor. Analyze this organization's progress and provide a concise, actionable summary.

Current Level: ${currentLevel} (${LEVEL_CONFIG[currentLevel]?.label || "Unknown"})
Maturity Score: ${latestResult?.maturity_score || "N/A"}%
Level 1 Score: ${latestResult?.level1_score || "N/A"}%
Level 2 Score: ${latestResult?.level2_score || "N/A"}%
Level 3 Score: ${latestResult?.level3_score || "N/A"}%

Task Progress:
- Total tasks: ${taskStats.total} (${taskStats.complete} complete)
- Level 1: ${taskStats.level1Complete}/${taskStats.level1} tasks complete
- Level 2: ${taskStats.level2Complete}/${taskStats.level2} tasks complete

Evidence Documents:
- Level 1: ${evidenceByLevel.level1} documents
- Level 2: ${evidenceByLevel.level2} documents
- Level 3: ${evidenceByLevel.level3} documents

Gaps Identified: ${latestResult?.gaps?.length > 0 ? latestResult.gaps.join(", ") : "None"}
Risk Flags: ${latestResult?.risk_flags?.length > 0 ? latestResult.risk_flags.join(", ") : "None"}

Provide a summary with exactly this JSON structure:
{
  "currentStatus": "One sentence describing where they are in the scheme",
  "achievements": ["List of 2-3 specific things they've accomplished"],
  "toCompleteCurrentLevel": ["2-3 specific actions to fully achieve current level"],
  "toReachNextLevel": ["2-3 specific actions to progress to the next level"],
  "priorityFocus": "The single most important area to focus on right now"
}`;

      const response = await base44.integrations.Core.InvokeLLM({
        prompt,
        response_json_schema: {
          type: "object",
          properties: {
            currentStatus: { type: "string" },
            achievements: { type: "array", items: { type: "string" } },
            toCompleteCurrentLevel: {
              type: "array",
              items: { type: "string" },
            },
            toReachNextLevel: { type: "array", items: { type: "string" } },
            priorityFocus: { type: "string" },
          },
          required: [
            "currentStatus",
            "achievements",
            "toCompleteCurrentLevel",
            "toReachNextLevel",
            "priorityFocus",
          ],
        },
      });

      setSummary(response);
    } catch (e) {
      const unavailable =
        e?.status === 503 || /not configured/i.test(e?.message || "");
      if (unavailable) unavailableRef.current = true;
      else console.error("Failed to generate AI summary", e);
      setSummary({
        currentStatus: `You are currently at Level ${currentLevel} — ${LEVEL_CONFIG[currentLevel]?.label || "Not Started"}.`,
        achievements: ["Assessment completed", "Evidence vault initialized"],
        toCompleteCurrentLevel: [
          "Complete all outstanding tasks for this level",
          "Upload supporting evidence documents",
        ],
        toReachNextLevel: [
          "Review next level requirements",
          "Plan implementation strategy",
        ],
        priorityFocus: "Complete your current level tasks and gather evidence",
      });
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div
        className="rounded-2xl border border-border bg-gradient-to-r from-teal-50 to-amber-50 p-6 animate-pulse"
        role="status"
        aria-live="polite"
        aria-label="Loading AI summary"
      >
        <div className="h-6 bg-muted rounded w-1/3 mb-4" />
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-5/6" />
        </div>
        <span className="sr-only">
          Generating your personalized Disability Confident journey summary...
        </span>
      </div>
    );
  }

  const levelCfg = LEVEL_CONFIG[currentLevel] || LEVEL_CONFIG[0];
  const isMaxLevel = currentLevel >= 3;

  return (
    <div
      className="rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50 to-amber-50 p-6 shadow-sm"
      role="region"
      aria-labelledby="ai-summary-heading"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h2
            id="ai-summary-heading"
            className="text-lg font-bold text-teal-900"
          >
            Your Disability Confident Journey
          </h2>
          <p className="text-sm text-teal-700 mt-0.5">
            {summary?.currentStatus}
          </p>
        </div>
      </div>

      {/* Priority Focus */}
      <div className="mb-5 p-3 bg-white/60 rounded-xl border border-teal-100">
        <div className="flex items-center gap-2 mb-1">
          <Target className="w-4 h-4 text-teal-600" aria-hidden="true" />
          <span className="text-xs font-bold text-teal-800 uppercase tracking-wide">
            Priority Focus
          </span>
        </div>
        <p className="text-sm font-medium text-teal-900">
          {summary?.priorityFocus}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Achievements */}
        <div className="bg-white/60 rounded-xl p-4 border border-teal-100">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-4 h-4 text-teal-600" aria-hidden="true" />
            <h3
              id="achievements-heading"
              className="text-xs font-bold text-teal-800 uppercase tracking-wide"
            >
              Achievements
            </h3>
          </div>
          <ul className="space-y-1.5" aria-labelledby="achievements-heading">
            {summary?.achievements?.map((achievement, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-sm text-teal-900"
              >
                <span className="text-teal-500 mt-0.5" aria-hidden="true">
                  •
                </span>
                <span>{achievement}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* To Complete Current Level */}
        <div className="bg-white/60 rounded-xl p-4 border border-amber-100">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-amber-600" aria-hidden="true" />
            <h3
              id="current-level-heading"
              className="text-xs font-bold text-amber-800 uppercase tracking-wide"
            >
              {isMaxLevel
                ? "Maintain Excellence"
                : `Complete ${levelCfg.label}`}
            </h3>
          </div>
          <ul className="space-y-1.5" aria-labelledby="current-level-heading">
            {summary?.toCompleteCurrentLevel?.map((action, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-sm text-amber-900"
              >
                <span className="text-amber-500 mt-0.5" aria-hidden="true">
                  •
                </span>
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* To Reach Next Level */}
      {!isMaxLevel && (
        <div className="mt-4 bg-white/60 rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <ArrowRight className="w-4 h-4 text-slate-600" aria-hidden="true" />
            <h3
              id="next-level-heading"
              className="text-xs font-bold text-slate-800 uppercase tracking-wide"
            >
              Progress to {levelCfg.nextLabel}
            </h3>
          </div>
          <ul className="space-y-1.5" aria-labelledby="next-level-heading">
            {summary?.toReachNextLevel?.map((action, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-sm text-slate-900"
              >
                <span className="text-slate-500 mt-0.5" aria-hidden="true">
                  •
                </span>
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk Flags */}
      {latestResult?.risk_flags?.length > 0 && (
        <div
          className="mt-4 p-3 bg-red-50 rounded-xl border border-red-200"
          role="alert"
          aria-labelledby="risk-flags-heading"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle
              className="w-4 h-4 text-red-600 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <p
                id="risk-flags-heading"
                className="text-xs font-bold text-red-800 uppercase tracking-wide mb-1"
              >
                Risk Flags
              </p>
              <ul className="space-y-0.5" aria-labelledby="risk-flags-heading">
                {latestResult.risk_flags.map((flag, idx) => (
                  <li key={idx} className="text-sm text-red-700">
                    <span aria-hidden="true">•</span> {flag}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
